import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  createNgftAppRegistry,
  NotificationCentreButton,
  NotificationsProvider,
  type IamNotificationsClient,
} from "@ngft/react-components-library/notifications";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import useMediaQuery from "@mui/material/useMediaQuery";

import { type CrewContext, type UserContext } from "../../api/crew";
import useCrew from "../../hooks/useCrew";
import useFeatureFlag, { FEATURE_FLAGS } from "../../hooks/useFeatureFlag";
import useUser from "../../hooks/useUser";
import { getToken, getUserSub } from "../../services/auth";
import { crewRoutes, type CrewRouteContext } from "./notificationRoutes";

// Notification Centre of ngft-iam in the header. It talks to REACT_APP_IAM_URL when the
// "notification-centre" GrowthBook flag is on; REACT_APP_NOTIFICATIONS=fake uses the library's
// in-memory IAM instead (local development and tests).
const PRODUCT = "crew";

type Mode = "iam" | "fake";

// The library sends the user's access token to this URL: https only, http just for a local IAM.
export const isAllowedIamUrl = (url: string) => {
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === "https:" || (protocol === "http:" && hostname === "localhost");
  } catch {
    return false;
  }
};

// REACT_APP_NOTIFICATIONS_SHARED_PRODUCT: a product shown next to "crew" (`ngft`: NGFT and company
// bulletins). Only a product key counts; anything else means none. Set it only where ngft-iam serves
// the shared feed: an older IAM answers 400 to `shared` and the centre would show nothing.
export const sharedProductFrom = (value: string | undefined) => {
  const product = value?.trim() ?? "";
  return /^[a-z][a-z0-9_]{0,31}$/.test(product) ? product : undefined;
};

// Whether the provider above is live (flag on and a valid URL, or fake mode with its client loaded).
const NotificationsLiveContext = createContext(false);

/**
 * One provider for the whole header, so the desktop and the mobile bell share one sync engine
 * (one poll, one toast per new notification). While notifications are off it stays mounted with
 * no user, which loads nothing; toggling the flag therefore never remounts the header.
 */
export const CrewNotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { crew, setCrew } = useCrew() as { crew: CrewContext; setCrew: (crew: CrewContext) => void };
  const user = useUser() as UserContext;
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.NOTIFICATION_CENTRE);
  const [fakeClient, setFakeClient] = useState<IamNotificationsClient | null>(null);

  // Read at render time (CRA inlines them at build time anyway), so tests can switch the mode.
  const mode: Mode | null =
    process.env.REACT_APP_NOTIFICATIONS === "fake"
      ? "fake"
      : isEnabled && isAllowedIamUrl(process.env.REACT_APP_IAM_URL ?? "")
        ? "iam"
        : null;
  const iamUrl = mode === "iam" ? (process.env.REACT_APP_IAM_URL ?? "") : "";
  const shared = sharedProductFrom(process.env.REACT_APP_NOTIFICATIONS_SHARED_PRODUCT);
  // Other NGFT apps a notification can open: a bulletin opens its post on my.ngft.com (the
  // Cockpit, REACT_APP_DASHBOARD_URI). The library opens https URLs only.
  const myNgftUrl = process.env.REACT_APP_DASHBOARD_URI || undefined;
  const apps = useMemo(() => createNgftAppRegistry({ myNgftUrl }), [myNgftUrl]);

  // Nothing is read from Keycloak while notifications are off.
  const userId = mode ? getUserSub() : null;
  const selectedCompanyId = crew?.globalId ?? null;
  const companies = useMemo(
    () =>
      (user?.crews ?? [])
        .filter((c): c is CrewContext & { globalId: string } => Boolean(c.globalId))
        .map((c) => ({ id: c.globalId, name: c.name })),
    [user?.crews],
  );

  useEffect(() => {
    if (mode !== "fake" || !userId) return;
    let active = true;
    import("./fakeIam").then(({ getFakeIamClient }) => {
      if (active) setFakeClient(getFakeIamClient(userId, PRODUCT, companies, shared));
    });
    return () => {
      active = false;
    };
  }, [mode, userId, companies, shared]);

  const isLive = mode === "iam" || (mode === "fake" && fakeClient !== null);

  return (
    <NotificationsProvider<CrewRouteContext>
      product={PRODUCT}
      sharedProduct={shared}
      iamBaseUrl={iamUrl}
      getAccessToken={getToken}
      userId={isLive ? userId : null}
      selectedCompanyId={selectedCompanyId}
      // Crew routes are built from the selected crew, so a click waits until one is selected.
      ready={crew?.id != null}
      companies={companies}
      routes={crewRoutes}
      apps={apps}
      locale={i18n.language}
      navigationKey={location.pathname}
      client={mode === "fake" ? (fakeClient ?? undefined) : undefined}
      host={{
        switchCompany: (companyId) => {
          const next = user?.crews?.find((c) => c.globalId === companyId);
          if (next) setCrew({ ...next, baseIds: null, baseName: null });
        },
        routeContext: () => ({ crewId: String(crew?.id ?? "") }),
        navigate: (href) => history.push(href),
        notify: (text, kind) => enqueueSnackbar(text, { variant: kind }),
      }}
    >
      <NotificationsLiveContext.Provider value={isLive}>{children}</NotificationsLiveContext.Provider>
    </NotificationsProvider>
  );
};

// The header's CSS (index.scss) shows its desktop part above 1025px and its mobile part below.
// Only the visible bell renders: both share the provider's open state and "Different company"
// dialog, so a hidden second bell would open a second popover and dialog.
const PLACEMENT_HIDDEN_QUERY = {
  desktop: "(max-width: 1025px)",
  mobile: "(min-width: 1025px)",
} as const;

const CrewNotificationBell = ({ placement }: { placement: keyof typeof PLACEMENT_HIDDEN_QUERY }) => {
  const isLive = useContext(NotificationsLiveContext);
  const isHidden = useMediaQuery(PLACEMENT_HIDDEN_QUERY[placement]);
  if (!isLive || isHidden) return null;
  // The padding of the header's other small icon buttons (cart, logout, cockpit), so the bell is the same size.
  return <NotificationCentreButton sx={{ p: "5px" }} />;
};

export default CrewNotificationBell;
